<template>
  <div>
    <v-btn v-if="isAdd" color="accent" small @click.stop="dialog = !dialog"> add </v-btn>
    <v-btn v-else @click.stop="dialog = !dialog" color="secondary"> edit </v-btn>
    <v-dialog
      v-if="dialog"
      v-model="dialog"
      scrollable
      max-width="500"
      transition="dialog-bottom-transition"
      @click:outside="resetForm"
    >
      <v-card>
        <v-card-title class="grey lighten-4 py-4 title black--text">
          <div>{{ isAdd ? 'Add new' : 'Edit' }} B2B Assessment Package</div>
          <v-spacer></v-spacer>
          <v-card-actions v-if="!isAdd">
            <v-btn icon @click="deleteB2BAssessmentPackage"><v-icon color="red">delete</v-icon></v-btn>
          </v-card-actions>
        </v-card-title>
        <v-card-text>
          <v-form v-model="Form.valid" ref="b2bAssessmentPackageForm">
            <v-container grid-list-xs :class="$vuetify.breakpoint.mdAndUp ? 'pa-5' : ''">
              <v-layout row wrap>
                <v-flex xs12>
                  <div class="text-subtitle-2 mb-4">Select Organization</div>
                  <v-autocomplete
                    outlined
                    v-model="Form.organization_id"
                    :rules="[(v) => !!v || 'Organization is required']"
                    required
                    :items="corporateList"
                    item-text="name"
                    item-value="id"
                    label="Organization"
                    :disabled="!isAdd"
                  />
                </v-flex>
                <v-flex xs12>
                  <v-select
                    :items="packageTypeSelect"
                    item-text="text"
                    item-value="value"
                    persistent-hint
                    label="Package Type"
                    required
                    v-model="Form.package_type"
                    :rules="[(v) => !!v || 'Package Type is required']"
                  />
                </v-flex>
                <v-flex xs12>
                  <v-text-field
                    label="Package Name"
                    v-model="Form.name"
                    :rules="[(v) => !!v || 'Package Name is required']"
                    required
                  />
                </v-flex>
                <v-flex xs12>
                  <v-textarea label="Description" v-model="Form.description" rows="3" outlined />
                </v-flex>
                <v-flex xs12>
                  <v-text-field
                    label="Duration (minutes)"
                    v-model="Form.duration_minute"
                    @keypress="onlyNumber"
                    :rules="[(v) => !!v || 'Duration is required']"
                    required
                  />
                </v-flex>

                <!-- Start Date -->
                <v-flex xs12>
                  <v-menu
                    v-model="startDateMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    max-width="290px"
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        v-model="Form.start_at"
                        label="Start Date"
                        prepend-icon="event"
                        readonly
                        v-on="on"
                        :rules="[(v) => !!v || 'Start Date is required']"
                      />
                    </template>
                    <v-date-picker v-model="Form.start_at" no-title @input="startDateMenu = false" />
                  </v-menu>
                </v-flex>

                <!-- End Date -->
                <v-flex xs12>
                  <v-menu
                    v-model="endDateMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    max-width="290px"
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        v-model="Form.end_at"
                        label="End Date"
                        prepend-icon="event"
                        readonly
                        v-on="on"
                        :rules="[(v) => !!v || 'End Date is required', validateEndDate]"
                      />
                    </template>
                    <v-date-picker v-model="Form.end_at" no-title @input="endDateMenu = false" :min="Form.start_at" />
                  </v-menu>
                </v-flex>

                <v-flex>
                  <v-card-actions>
                    <v-btn color="accent" :disabled="!Form.valid" @click="addOrEditB2BAssessmentPackage">{{
                      isAdd ? 'Create' : 'Update'
                    }}</v-btn>
                  </v-card-actions>
                </v-flex>
              </v-layout>
            </v-container>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Forms from '../form/form';
import b2bAssessmentPackageService from '../services/b2bassessmentpackage';
import moment from 'moment-timezone';

export default {
  data() {
    return {
      dialog: false,
      packageId: null,
      startDateMenu: false,
      endDateMenu: false,
      Form: new Forms({
        data: () => ({
          organization_id: null,
          package_type: '',
          name: '',
          description: '',
          duration_minute: 0,
          start_at: null,
          end_at: null,
        }),
      }),
      packageTypeSelect: [
        { text: 'Screen', value: 'screen' },
        { text: 'Holistic', value: 'holistic' },
        { text: 'Focus', value: 'focus' },
      ],
    };
  },
  props: {
    isAdd: {
      type: Boolean,
      default: true,
    },
    corporateList: {
      type: Array,
      default: () => [],
    },
    b2bAssessmentPackage: {
      type: Object,
      default: () => {},
    },
  },
  computed: {
    validateEndDate() {
      return () => {
        if (!this.Form.start_at || !this.Form.end_at) return true;
        return new Date(this.Form.end_at) > new Date(this.Form.start_at) || 'End date must be after start date';
      };
    },
  },
  methods: {
    resetForm() {
      this.Form.reset();
      this.startDateMenu = false;
      this.endDateMenu = false;
    },
    onlyNumber(e) {
      const char = String.fromCharCode(e.keyCode);
      if (!/[0-9]/.test(char)) {
        e.preventDefault();
      }
    },
    fillForm() {
      this.$nextTick(() => {
        this.$refs.b2bAssessmentPackageForm.resetValidation();
      });
      this.packageId = this.b2bAssessmentPackage.id;
      this.Form.organization_id = this.b2bAssessmentPackage.organization_id;
      this.Form.package_type = this.b2bAssessmentPackage.package_type;
      this.Form.name = this.b2bAssessmentPackage.name;
      this.Form.description = this.b2bAssessmentPackage.description;
      this.Form.duration_minute = this.b2bAssessmentPackage.duration_minute;

      // Format dates for the date picker (YYYY-MM-DD)
      if (this.b2bAssessmentPackage.start_at) {
        this.Form.start_at = this.b2bAssessmentPackage.start_at.split('T')[0];
      }
      if (this.b2bAssessmentPackage.end_at) {
        this.Form.end_at = this.b2bAssessmentPackage.end_at.split('T')[0];
      }
    },

    preparePayload() {
      const startISO = moment(this.Form.start_at).startOf('day').toISOString();
      const endISO = moment(this.Form.end_at).endOf('day').toISOString();
      const payload = {
        organization_id: this.Form.organization_id,
        package_type: this.Form.package_type,
        name: this.Form.name,
        description: this.Form.description,
        duration_minute: parseInt(this.Form.duration_minute),
        start_at: startISO,
        end_at: endISO,
      };
      return payload;
    },

    async addOrEditB2BAssessmentPackage() {
      if (this.Form.valid) {
        const payload = this.preparePayload();
        if (this.isAdd) {
          b2bAssessmentPackageService
            .createB2BAssessmentPackage(payload)
            .then(() => {
              this.dialog = false;
              this.resetForm();
              this.$emit('success');
              this.$notify({
                title: 'Success',
                text: 'New B2B assessment package has been added successfully.',
                type: 'success',
              });
            })
            .catch((error) => {
              console.error('Error creating new B2B assessment package:', error);
              this.$notify({
                title: 'Error',
                text: `There was an error adding the new B2B assessment package. Please try again. \n${
                  error.response?.data?.error?.message || error.message
                }`,
                type: 'error',
              });
            });
        } else {
          b2bAssessmentPackageService
            .updateB2BAssessmentPackage(this.packageId, payload)
            .then(() => {
              this.dialog = false;
              this.resetForm();
              this.$emit('success');
              this.$notify({
                title: 'Success',
                text: 'B2B assessment package has been updated successfully.',
                type: 'success',
              });
            })
            .catch((error) => {
              console.error('Error updating B2B assessment package:', error);
              this.$notify({
                title: 'Error',
                text: `There was an error updating the B2B assessment package. Please try again. \n${
                  error.response?.data?.error?.message || error.message
                }`,
                type: 'error',
              });
            });
        }
      }
    },
    async deleteB2BAssessmentPackage() {
      this.$root
        .$confirm(
          'Delete B2B Assessment Package',
          `Are you sure you want to delete this B2B assessment package for <b>${this.b2bAssessmentPackage.organization_name}</b>? This action cannot be undone.`,
          { color: 'red' }
        )
        .then(async (confirm) => {
          if (confirm) {
            await b2bAssessmentPackageService
              .deleteB2BAssessmentPackage(this.packageId)
              .then(() => {
                this.dialog = false;
                this.resetForm();
                this.$emit('success');
                this.$notify({
                  title: 'Success',
                  text: 'B2B assessment package has been deleted successfully.',
                  type: 'success',
                });
              })
              .catch((error) => {
                console.error('Error deleting B2B assessment package:', error);
                this.$notify({
                  title: 'Error',
                  text: 'There was an error deleting the B2B assessment package. Please try again.',
                  type: 'error',
                });
              });
          }
        });
    },
  },
  watch: {
    dialog(val) {
      if (val && !this.isAdd && this.b2bAssessmentPackage) {
        this.fillForm();
      }
    },
  },
};
</script>
